import React, { useEffect, useRef } from "react";
import { useCart } from "../../context/CartContext";
import {
  Box,
  Text,
  Button,
  Image,
  Grid,
  GridItem,
  IconButton,
  useToast,
  Flex,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Icon,
  useDisclosure,
} from "@chakra-ui/react";
import { FaTrash } from "react-icons/fa";
import { FiBriefcase } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import MerchantSwitchConfirmation from "../Dashboard/Merchant/MerchantSwitchConfirmation";

import QuantityInput from "./QuantityInput"

const Cart = () => {
  const { cartItems, removeFromCart, clearCart, changeQuantity, updateCart } = useCart();
  const auth = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [switching, setSwitching] = React.useState(false);
  const cancelRef = useRef();

  useEffect(() => {
    if (cartItems.length > 0) {
      updateCart();
    }
  }, [updateCart, cartItems.length]); // Include cartItems.length dependency

  const totalAmount = cartItems.reduce((total, item) => total + item.price_per_share * item.quantity, 0);

  const handleSwitchToMerchant = async () => {
    setSwitching(true);
    try {
      await auth.switchBackToMerchant();

      toast({
        title: 'Switched to Merchant View',
        description: 'You can now complete your purchase',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onClose();
    } catch (error) {
      console.error('Error switching to merchant:', error);
      toast({
        title: 'Switch Failed',
        description: 'Failed to switch to merchant view',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSwitching(false);
    }
  };

  const handleCheckout = () => {
    updateCart()
    let checkAvailability = cartItems.findIndex((item) => item.quantity > item.available_shares);

    if(checkAvailability !== -1){
      toast({
        title: "Item quantity is more than available streamshares",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (auth.isAuthenticated) {
      // Check if user is a linked artist
      if (isLinkedArtist) {
        onOpen(); // Open switch to merchant dialog
        return;
      }

      // Check if user is trying to purchase their own tracks
      const ownTrackIndex = cartItems.findIndex((it) => it.publisher.id === auth.user.user_id);
      if (ownTrackIndex !== -1) {
        toast({
          title: "Cannot purchase your own track",
          description: "Please remove your own tracks from basket before checkout.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        navigate("/checkout");
      }
    } else {
      navigate("/login");
    }
  };

  const handleQuantityChange = (index, value) => {
    // Handle empty string (when user clears the input)
    if (value === "" || value === null || value === undefined) {
      changeQuantity(index, 0);
      return;
    }

    // Parse the value
    const numValue = typeof value === 'number' ? value : parseInt(value, 10);

    // Validate and update
    if (!isNaN(numValue) && numValue >= 0) {
      changeQuantity(index, numValue);
    }
  };

  // Check if user is viewing in artist context (either switched from merchant or logged in as linked artist)
  // This matches the same logic used in the profile menu
  const isLinkedArtist = React.useMemo(() => {
    if (!auth.isAuthenticated) {
      return false;
    }

    // Check if merchant has switched to artist context
    if (auth.isSwitchedContext) {
      return true;
    }

    // Check if logged in as a linked artist (artist created by merchant)
    if (auth.user?.account_type === 'artist') {
      return !!(
        auth.user?.merchant_id ||
        auth.user?.account?.merchant_id ||
        auth.user?.account?.profile?.merchant_id
      );
    }

    return false;
  }, [auth.isAuthenticated, auth.isSwitchedContext, auth.user]);

  return (
    <Box p={4} borderRadius="md" boxShadow="md">
      {/* Show alert for linked artists */}
      {isLinkedArtist && cartItems.length > 0 && (
        <Alert status="warning" mb={4} borderRadius="md" bg="orange.100" color="orange.900">
          <AlertIcon color="orange.500" />
          <Box flex="1">
            <AlertTitle fontSize="md" fontWeight="bold">Linked Artist Account</AlertTitle>
            <AlertDescription fontSize="sm" display="block">
              Your account is managed by a merchant. Switch to your merchant account to complete this purchase.
            </AlertDescription>
          </Box>
          <Button
            size="sm"
            colorScheme="orange"
            leftIcon={<Icon as={FiBriefcase} />}
            onClick={onOpen}
            ml={2}
          >
            Switch to Merchant
          </Button>
        </Alert>
      )}

      <Grid
        templateColumns={{ base: "1fr 3fr", md: "1fr 1fr 1fr 2fr" }}
        gap={0}
        p={5}
        templateRows={{ base: "1fr 1fr", sm: "1fr" }}
        align="center"
        justifyItems={{base:"center"}}
        bg="secondary.900"
        color="primary.100"
        borderRadius="md"
        boxShadow="md"
        display={{ base: "none", md: "grid" }}
      >
        <GridItem >
          <Text fontSize="lg">Item</Text>
        </GridItem>
        <GridItem justifySelf="center" >
          <Text fontSize="lg">Available streamshares</Text>
        </GridItem>
        <GridItem >
          <Text fontSize="lg">Price</Text>
        </GridItem>
        <GridItem >
          <Text fontSize="lg">Quantity</Text>
        </GridItem>
      </Grid>
      {cartItems.length === 0 ? (
        <Text my={2} color="white">Your basket is empty.</Text>
      ) : (
        <Box>
          {cartItems.map((item, index) => (
            <Grid
              key={index}
              templateColumns={{ base: "1fr 3fr", md: "1fr 1fr 1fr 2fr" }}
              templateRows={{ base: "2fr 1fr 1fr", md: "1fr" }}
              gap={{base:3, md:1}}
              p={{base:4,md:3}}
              align={{base:"left", md:"center"}}
              alignItems="center"
              justifyItems={{base:"flex-start", md:"center"}}
              bg={auth.isAuthenticated && auth.user && item.publisher.id === auth.user.user_id ? "#770737" : "secondary.100"}
              borderRadius="md"
              boxShadow="md"
              color="primary.900"
              m={3}

            >

              <GridItem justifySelf="flex-start" gridColumn={{ base: "1/2", md: "1/2" }} gridRow={{ base: "1/4" , md:"1/2"}} w={{base:"auto"}} display="flex" alignItems="center" justifyContent="flex-start">
                <Image src={item.image_url} alt={item.track_name} borderRadius="lg" w="4rem" />
                <Box ml={{base:4,md:4}}>
                  <Text display={{ base: "none", md: "block" }}>{item.track_name}</Text>
                  {auth.isAuthenticated && auth.user && item.publisher.id === auth.user.user_id && (
                    <Text fontSize="xs" color="red.300" fontWeight="bold" display={{ base: "none", md: "block" }}>
                      (Your Track)
                    </Text>
                  )}
                </Box>
              </GridItem >
              <GridItem gridColumn="2/3"  display={{ base: "none", md: "block" }} gridRow={{ base: "2/3" , md:"1/2"}}>
                <Text fontSize="lg">{item.available_shares}</Text>
              </GridItem>
              <GridItem gridColumn={{ base: "2/3", md: "3/4" }} gridRow={{ base: "1/4" , md:"1/2"}} >
                <Box>
                  <Text display={{ base: "block", md: "none" }} ml={{base:0,md:4}}>{item.track_name}</Text>
                  {auth.isAuthenticated && auth.user && item.publisher.id === auth.user.user_id && (
                    <Text fontSize="xs" color="red.300" fontWeight="bold" display={{ base: "block", md: "none" }}>
                      (Your Track)
                    </Text>
                  )}
                  <Text fontSize={{ base: "2xl", md: "lg" }} >${item.price_per_share}</Text>
                </Box>
              </GridItem>

              <GridItem gridColumn={{ base: "1/3", md: "4/5" }} gridRow={{ base: "4/5" , md:"1/2"}} display="flex" alignItems="center" justifyContent="space-between" >

                <QuantityInput
                  value={item.quantity === 0 ? "" : item.quantity}
                  min={0}
                  max={item.available_shares}
                  onChange={(value) => handleQuantityChange(index, value)}
                  color={item.quantity > item.available_shares ? "red" : "primary.100"}
                />
                <IconButton
                  icon={<FaTrash />}
                  colorScheme="red"
                  size="sm"
                  ml={{base:2, md:4}}
                  onClick={() => removeFromCart(index)}
                  aria-label={`Remove ${item.track_name} from basket`}
                />
              </GridItem>
            </Grid>
          ))}
          <Flex justifyContent="space-between" alignItems="center" m={4}>
            <Box>
              <Text fontWeight="bold" color="white">Total: ${totalAmount.toFixed(2)}</Text>
              <Button size="sm" mt={2} onClick={clearCart}>Clear Basket</Button>
            </Box>
            <Button
              size="lg"
              colorScheme={isLinkedArtist ? "orange" : "green"}
              onClick={handleCheckout}
              leftIcon={isLinkedArtist ? <Icon as={FiBriefcase} /> : undefined}
            >
              {isLinkedArtist ? "Switch to Merchant" : "Checkout"}
            </Button>
          </Flex>
        </Box>
      )}

      {/* Merchant Switch Confirmation Dialog */}
      <MerchantSwitchConfirmation
        isOpen={isOpen}
        onClose={onClose}
        merchantName={auth.user?.company_name}
        onConfirm={handleSwitchToMerchant}
        isLoading={switching}
        cancelRef={cancelRef}
      />
    </Box>
  );
};

export default Cart;
